import React, { useCallback, useMemo, useState } from 'react';

import { Input, Menu, Popover } from '@arco-design/web-react';
import { ToolItem } from '../ToolItem';
import { IconFont, useBlock, useFocusIdx } from '@bay-email/editor';
import { get } from 'lodash';
import { useTiptapEditor } from '../Tools/TiptapEditorContext';
import { useFontFamily } from '@extensions/hooks/useFontFamily';
import { t } from '@bay-email/core';
import { getSelectionTextStyle } from '../Tools/selectionTextStyle';
import styleText from '../../styles/ToolsPopover.css?inline';

export interface FontFamilyProps {
	execCommand: (cmd: string, value: any) => void;
	getPopupContainer: () => HTMLElement;
	onBeforeInteract?: () => void;
}

export function FontFamily(props: FontFamilyProps) {
	const { fontGroups } = useFontFamily();
	const { execCommand, onBeforeInteract } = props;
	const editor = useTiptapEditor();
	const { focusIdx } = useFocusIdx();
	const { values } = useBlock();
	// What the selection renders in (inline font, else block default); '' when mixed.
	const currentFamily = editor
		? getSelectionTextStyle(
				editor,
				'fontFamily',
				focusIdx ? String(get(values, `${focusIdx}.attributes.font-family`) || '') : '',
			)
		: '';
	const selectedFamilyKey =
		fontGroups
			.flatMap(group => group.options)
			.find(
				item =>
					currentFamily &&
					String(item.value)
						.split(',')[0]
						.trim()
						.replace(/^['"]|['"]$/g, '')
						.toLowerCase() === currentFamily.toLowerCase(),
			)?.value || '';
	const [visible, setVisible] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');

	const filteredFontGroups = useMemo(() => {
		const normalizedSearch = searchQuery.trim().toLowerCase();
		if (!normalizedSearch) return fontGroups;

		return fontGroups
			.map(group => ({
				...group,
				options: group.options.filter(item =>
					item.plainLabel.toLowerCase().includes(normalizedSearch),
				),
			}))
			.filter(group => group.options.length > 0);
	}, [fontGroups, searchQuery]);

	const hasResults = filteredFontGroups.some(group => group.options.length > 0);

	const onChange = useCallback(
		(val: string) => {
			onBeforeInteract?.();
			execCommand('fontName', val);
			setSearchQuery('');
			setVisible(false);
		},
		[execCommand, onBeforeInteract],
	);

	const onVisibleChange = useCallback(
		(v: boolean) => {
			if (v) {
				onBeforeInteract?.();
			}
			if (!v) {
				setSearchQuery('');
			}
			setVisible(v);
		},
		[onBeforeInteract],
	);

	return (
		<Popover
			trigger='click'
			color='#fff'
			position='bl'
			className='bay-email-extensions-Tools-Popover'
			popupVisible={visible}
			onVisibleChange={onVisibleChange}
			content={
				<>
					<style>{styleText}</style>
					<div
						onMouseDownCapture={() => onBeforeInteract?.()}
						onKeyDownCapture={() => onBeforeInteract?.()}
						style={{
							maxWidth: 150,
							maxHeight: 350,
							overflowY: 'auto',
							overflowX: 'hidden',
						}}
					>
						<div
							onMouseDown={evt => evt.stopPropagation()}
							style={{
								padding: '8px 8px 6px',
							}}
						>
							<Input.Search
								allowClear
								placeholder={t('Search')}
								value={searchQuery}
								onFocus={() => onBeforeInteract?.()}
								onChange={value => {
									onBeforeInteract?.();
									setSearchQuery(value);
								}}
								onSearch={value => {
									onBeforeInteract?.();
									setSearchQuery(value);
								}}
								size='small'
							/>
						</div>
						{filteredFontGroups.map((group, groupIdx) => (
							<React.Fragment key={group.key}>
								{groupIdx > 0 && (
									<div
										style={{
											borderTop: '1px solid var(--color-border-2)',
											margin: '6px 8px 4px',
										}}
									/>
								)}
								<div
									style={{
										padding: '4px 12px 6px',
										fontSize: 11,
										fontWeight: 600,
										textTransform: 'uppercase',
										opacity: 0.75,
									}}
								>
									{group.label}
								</div>
								<Menu
									onClickMenuItem={onChange}
									selectedKeys={selectedFamilyKey ? [selectedFamilyKey] : []}
									style={{ border: 'none', padding: 0 }}
								>
									{group.options.map(item => (
										<Menu.Item
											style={{ lineHeight: '30px', height: 30 }}
											key={item.value}
										>
											{item.label}
										</Menu.Item>
									))}
								</Menu>
							</React.Fragment>
						))}
						{!hasResults && (
							<div
								style={{
									padding: '10px 12px 12px',
									fontSize: 12,
									color: '#667085',
								}}
							>
								{t('No fonts found')}
							</div>
						)}
					</div>
				</>
			}
			getPopupContainer={props.getPopupContainer}
		>
			<ToolItem
				title={t('Font Family')}
				style={currentFamily ? { width: 'auto', padding: '0 6px', gap: 4 } : undefined}
				icon={
					<>
						<IconFont iconName='icon-font-family' />
						{currentFamily && (
							<span
								style={{
									maxWidth: 90,
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									fontSize: 12,
								}}
							>
								{currentFamily}
							</span>
						)}
					</>
				}
			/>
		</Popover>
	);
}
